/* © CNES */
import { QueryFunctionContext } from '@tanstack/query-core';
import { useQuery } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';
import { Processing } from '../../models/Processing';
import { ProcessingResponse } from '../../models/ProcessingResponse';
import { apiConfiguration } from '../APIConfiguration';
import { authenticationAPI } from '../authentication/AuthenticateAPI';
import { handleHttpError } from '../common';
import responseMock from '../mock/response_processings.json';
import responseReportMock from '../mock/response_report.json';

/**
 * Interface for getProcessingsByUserLoggedIn parameters.
 */
interface ProcessingQueryModel {
  isEnabled: boolean;
}

export interface QueryQuickLookModel {
  jobId: string;
  processName: string;
  productId: string;
}

/**
 * Interface for getProcessingsByUserLoggedIn parameters.
 */
interface ProcessingReportQueryModel {
  jobId: string;
}

/**
 * Get all Processings for a single User that logged in the application.
 * @param queryKey the query parameters.
 * @returns a Promise containing a list of processings (jobs) for the logged in user.
 */
export const getProcessingsByUserLoggedIn = async ({
  queryKey,
}: QueryFunctionContext<[string, NavigateFunction, Function]>) => {
  let res: Response | undefined;
  const [, navigate, setError400] = queryKey;

  if (process.env.NODE_ENV === 'production') {
    res = await fetch(`${apiConfiguration.apiBaseUrl}/processing/jobs`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authenticationAPI.getAuthorizationHeaders(),
      },
    });
    handleHttpError(res, navigate, setError400);
  } else {
    res = new Response(JSON.stringify(responseMock));
  }

  return res.json();
};

/**
 * refetch Jobs.
 * @param data ProcessingResponse.
 * @returns needToRefetch Boolean is refetch needed.
 */
const refetchJobs = (data: ProcessingResponse) => {
  let needToRefetch = false;
  // refetch if no jobs found because some of them could be from CMD
  if (data?.jobs?.length === 0) {
    needToRefetch = true;
  }
  // refetch if process is running to update progression
  needToRefetch = data?.jobs?.some((job: Processing) => {
    return (
      job.status === 'running' ||
      job.status === 'accepted' ||
      job.status === 'dismissed'
    );
  });
  return needToRefetch;
};

export const useGetProcessings = (
  queryParams: ProcessingQueryModel,
  navigate: NavigateFunction,
  setError400: Function
) => {
  const queryKey = ['ProcessingsByUser', navigate, setError400] as [
    string,
    NavigateFunction,
    Function
  ];

  return useQuery({
    queryKey: queryKey,
    queryFn: getProcessingsByUserLoggedIn,
    enabled: queryParams.isEnabled, // comment to view in local env
    // Refetch the data every second
    refetchInterval: (data: any) => (refetchJobs(data) ? 5000 : false),
  });
};

/**
 * Get the Processing report for a job id.
 * @param queryKey the query parameters.
 * @returns a Promise containing a list of processings (jobs) for the logged in user.
 */
export const getProcessingReport = async ({
  queryKey,
}: QueryFunctionContext<[string, ProcessingReportQueryModel | undefined]>) => {
  let res: Response | undefined;
  const [, queryParams] = queryKey;

  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/processing-access-data/logs/report-${queryParams?.jobId}.json`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
      }
    );

    if (res.status === 404) {
      res = new Response(
        JSON.stringify({
          USER_INFO: {
            errorMessage: 'report not found.',
          },
        })
      );
    }
  } else {
    res = new Response(JSON.stringify(responseReportMock));
  }
  return res.json();
};

export const getProcessingQuicklook = async (
  queryKey: [
    string,
    QueryQuickLookModel | undefined,
    NavigateFunction,
    Function
  ]
) => {
  let res: Response | undefined;
  const [, queryParams, navigate, setError400] = queryKey;

  if (process.env.NODE_ENV === 'production') {
    res = await fetch(
      `${apiConfiguration.apiBaseUrl}/processing-access-data/${queryParams?.processName}/${queryParams?.jobId}-${queryParams?.productId}-quicklook.tif`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authenticationAPI.getAuthorizationHeaders(),
        },
      }
    );
    handleHttpError(res, navigate, setError400);
  } else
    res = await fetch('https://openlayers.org/data/raster/no-overviews.tif');

  return res;
};
